/* =============================================================
   TanStack Query wrappers for everything that talks to the
   Azure Functions /api layer. Co-locating queries and mutations
   here gives us a single index of every server interaction in
   the app — useful for review.

   Hook signatures, return types, and query keys are preserved
   from the Supabase version so pages don't need to change.
   ============================================================ */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { OrgRole } from './database.types';
import { apiFetch } from './api';
import {
  allStageAverages,
  overallAverage,
  overallZoneCounts,
  type Responses,
} from './psychSafety';

// ============================================================
// Workspaces (orgs the current user belongs to)
// ============================================================

export interface MyWorkspace {
  id: string;
  name: string;
  kind: string | null;
  role: OrgRole;
  joined_at: string;
}

export function useMyWorkspaces(userId: string | undefined) {
  return useQuery<MyWorkspace[]>({
    queryKey: ['my-workspaces', userId],
    enabled: !!userId,
    queryFn: () => apiFetch<MyWorkspace[]>('/api/workspaces'),
  });
}

export interface OrgRow {
  id: string;
  name: string;
  kind: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export function useCreateWorkspace() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { name: string; kind: string | null }) =>
      apiFetch<OrgRow>('/api/workspaces', {
        method: 'POST',
        body: { name: args.name, kind: args.kind },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['my-workspaces'] });
    },
  });
}

// ============================================================
// Workspace detail (single org + members list)
// ============================================================

export interface WorkspaceMember {
  user_id: string;
  role: OrgRole;
  joined_at: string;
  full_name: string | null;
  email?: string | null;
}

export function useWorkspace(orgId: string | undefined) {
  return useQuery<OrgRow>({
    queryKey: ['workspace', orgId],
    enabled: !!orgId,
    queryFn: () => apiFetch<OrgRow>(`/api/workspaces/${orgId}`),
  });
}

// ============================================================
// Psychological Safety Assessment — drafts + responses
// ============================================================

export interface AssessmentDraft {
  responses: Responses;
  stage_idx: number;
  updated_at: string;
}

export function useAssessmentDraft(userId: string | undefined) {
  return useQuery<AssessmentDraft | null>({
    queryKey: ['assessment-draft', userId],
    enabled: !!userId,
    queryFn: () => apiFetch<AssessmentDraft | null>('/api/assessments/draft'),
  });
}

export function useUpsertAssessmentDraft(userId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (draft: { responses: Responses; stage_idx: number }) => {
      if (!userId) throw new Error('not signed in');
      await apiFetch('/api/assessments/draft', {
        method: 'PUT',
        body: draft,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['assessment-draft', userId] });
    },
  });
}

export function useDeleteAssessmentDraft(userId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      if (!userId) throw new Error('not signed in');
      await apiFetch('/api/assessments/draft', { method: 'DELETE' });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['assessment-draft', userId] });
    },
  });
}

export interface CompletedResponse {
  id: string;
  responses: Responses;
  overall_score: number | null;
  stage_scores: Record<string, number> | null;
  zone_counts: Record<string, number> | null;
  completed_at: string;
  org_id: string | null;
}

/* One-shot landing snapshot for /assessment. Backed by a single
   stored proc that returns draft summary + latest + cooldown +
   workspaces in one round-trip. Replaces four parallel queries. */
export interface PsychLandingSnapshot {
  draft:
    | { has: true; answered_count: number; stage_idx: number; updated_at: string | null }
    | { has: false };
  latest: {
    id: string;
    completed_at: string;
    overall_score: number | null;
    org_id: string | null;
  } | null;
  total_count: number;
  cooldown: {
    can_take: boolean;
    last_completed_at: string | null;
    next_available_at: string | null;
  };
  workspaces: MyWorkspace[];
}

export function usePsychLandingSnapshot(userId: string | undefined) {
  return useQuery<PsychLandingSnapshot>({
    queryKey: ['psych-landing', userId],
    enabled: !!userId,
    /* Stable-ish data. Bump stale time so quick nav doesn't refetch. */
    staleTime: 5 * 60_000,
    queryFn: () => apiFetch<PsychLandingSnapshot>('/api/assessments/landing-snapshot'),
  });
}

/* One-shot landing snapshot for /dashboard. */
export interface DashboardSnapshot {
  is_approved: boolean;
  is_app_admin: boolean;
  pending_approvals_count: number;
  workspaces: MyWorkspace[];
}

export function useDashboardSnapshot(userId: string | undefined) {
  return useQuery<DashboardSnapshot>({
    queryKey: ['dashboard-snapshot', userId],
    enabled: !!userId,
    staleTime: 5 * 60_000,
    queryFn: () => apiFetch<DashboardSnapshot>('/api/dashboard-snapshot'),
  });
}

/* Lightweight landing-page snapshot: last-completed date + score +
   org tag + total count. Avoids downloading every prior response's
   full JSON just to render the header on /assessment. */
export interface LatestSummary {
  id: string;
  completed_at: string;
  overall_score: number | null;
  org_id: string | null;
  total_count: number;
}

export function useLatestAssessmentSummary(userId: string | undefined) {
  return useQuery<LatestSummary | null>({
    queryKey: ['assessment-latest-summary', userId],
    enabled: !!userId,
    queryFn: () => apiFetch<LatestSummary | null>('/api/assessments/latest-summary'),
  });
}

export function useLatestAssessmentResponse(userId: string | undefined) {
  return useQuery<CompletedResponse | null>({
    queryKey: ['assessment-latest', userId],
    enabled: !!userId,
    queryFn: () => apiFetch<CompletedResponse | null>('/api/assessments/latest'),
  });
}

export function useResponseHistory(userId: string | undefined) {
  return useQuery<CompletedResponse[]>({
    queryKey: ['assessment-history', userId],
    enabled: !!userId,
    queryFn: () => apiFetch<CompletedResponse[]>('/api/assessments/history'),
  });
}

export function useResponseById(id: string | undefined) {
  return useQuery<CompletedResponse | null>({
    queryKey: ['assessment-response', id],
    enabled: !!id,
    queryFn: () => apiFetch<CompletedResponse | null>(`/api/assessments/${id}`),
  });
}

export function useSubmitAssessmentResponse(userId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { responses: Responses; org_id?: string | null }) => {
      if (!userId) throw new Error('not signed in');
      const responses = args.responses;
      const overall = overallAverage(responses);
      const stage_scores = allStageAverages(responses);
      const zc = overallZoneCounts(responses);
      const zone_counts = {
        red: zc.red,
        neutral: zc.neutral,
        blue: zc.blue,
        total: zc.total,
      };
      return apiFetch<CompletedResponse>('/api/assessments', {
        method: 'POST',
        body: {
          responses,
          overall_score: Number(overall.toFixed(2)),
          stage_scores,
          zone_counts,
          org_id: args.org_id ?? null,
        },
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['assessment-latest', userId] });
      qc.invalidateQueries({ queryKey: ['assessment-draft', userId] });
      qc.invalidateQueries({ queryKey: ['assessment-history', userId] });
      qc.invalidateQueries({ queryKey: ['assessment-can-take', userId] });
      qc.invalidateQueries({ queryKey: ['assessment-aggregate'] });
    },
  });
}

/* Retag a completed assessment to a group (or clear the tag).
   Doesn't count as a new assessment; the 90-day cooldown still
   holds for actual submissions. */
export function useRetagAssessment(userId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { id: string; org_id: string | null }) => {
      await apiFetch(`/api/assessments/${args.id}`, {
        method: 'PUT',
        body: { org_id: args.org_id },
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['assessment-latest', userId] });
      qc.invalidateQueries({ queryKey: ['assessment-history', userId] });
      qc.invalidateQueries({ queryKey: ['assessment-aggregate'] });
    },
  });
}

/* Server-side 90-day cooldown check + next-eligible date. */
export interface CanTakeAssessment {
  can_take: boolean;
  last_completed_at: string | null;
  next_available_at: string | null;
}

export function useCanTakeAssessment(userId: string | undefined) {
  return useQuery<CanTakeAssessment>({
    queryKey: ['assessment-can-take', userId],
    enabled: !!userId,
    queryFn: () => apiFetch<CanTakeAssessment>('/api/assessments/can-take'),
  });
}

/* Anonymized aggregate for a workspace: one row per member with a
   completed assessment tagged to that workspace. `is_mine` flags the
   caller's own row so the UI can highlight it. */
export interface AssessmentAggregatePoint {
  overall_score: number;
  is_mine: boolean;
  completed_at: string;
}

export function useOrgAssessmentAggregate(orgId: string | undefined) {
  return useQuery<AssessmentAggregatePoint[]>({
    queryKey: ['assessment-aggregate', orgId],
    enabled: !!orgId,
    queryFn: () =>
      apiFetch<AssessmentAggregatePoint[]>(
        `/api/workspaces/${orgId}/assessment-aggregate`,
      ),
  });
}

// ============================================================
// CYBHI Checklists — list / create / item state
// ============================================================

export interface ChecklistRow {
  id: string;
  org_id: string;
  name: string;
  pathway: 'A' | 'B' | 'C' | 'D' | null;
  sub_pathway_c: 'C1' | 'C2' | 'C3' | null;
  scenarios: string[];
  archived: boolean;
  /** Set once the billing-system question was answered (a Yes is also scenario S8). */
  billing_answered_at?: string | null;
  /** Single checklist only: may this person answer the billing question (creator, team owner or admin)? */
  can_answer?: boolean;
  created_at: string;
  updated_at: string;
}

export function useChecklists(orgId: string | undefined) {
  return useQuery<ChecklistRow[]>({
    queryKey: ['checklists', orgId],
    enabled: !!orgId,
    queryFn: () => apiFetch<ChecklistRow[]>(`/api/workspaces/${orgId}/checklists`),
  });
}

export function useChecklist(checklistId: string | undefined) {
  return useQuery<ChecklistRow | null>({
    queryKey: ['checklist', checklistId],
    enabled: !!checklistId,
    queryFn: () => apiFetch<ChecklistRow | null>(`/api/checklists/${checklistId}`),
  });
}

export function useArchiveChecklist() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { id: string; archived: boolean }) => {
      await apiFetch(`/api/checklists/${args.id}`, {
        method: 'PUT',
        body: { archived: args.archived },
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['checklists'] });
      qc.invalidateQueries({ queryKey: ['checklist'] });
    },
  });
}

export function useDeleteChecklist() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiFetch(`/api/checklists/${id}`, { method: 'DELETE' });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['checklists'] });
    },
  });
}

export function useCreateChecklist() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      org_id: string;
      name: string;
      pathway: 'A' | 'B' | 'C' | 'D';
      sub_pathway_c: 'C1' | 'C2' | 'C3' | null;
      scenarios: string[];
      owns_billing?: boolean | null;
    }) =>
      apiFetch<ChecklistRow>(`/api/workspaces/${args.org_id}/checklists`, {
        method: 'POST',
        body: {
          name: args.name,
          pathway: args.pathway,
          sub_pathway_c: args.sub_pathway_c,
          scenarios: args.scenarios,
          owns_billing: args.owns_billing ?? null,
        },
      }),
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: ['checklists', row.org_id] });
    },
  });
}

/** Answer "Do you own or operate your billing system?" for a saved checklist: changes only S8. */
/** The billing answer's save, and the read of the checklist after it: useIsMutating(billingKey(id)). */
export const billingKey = (checklistId: string | undefined) => ['checklist-billing', checklistId] as const;

export function useAnswerBillingQuestion(checklistId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: billingKey(checklistId),
    mutationFn: async (ownsBilling: boolean) => {
      await apiFetch(`/api/checklists/${checklistId}/billing`, { method: 'PUT', body: { owns_billing: ownsBilling } });
    },
    /* Returned, so the answer counts as saved only once the checklist has been read again. */
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: ['checklist', checklistId] }),
        qc.invalidateQueries({ queryKey: ['checklists'] }),
      ]),
  });
}

export interface ItemState {
  item_id: string;
  checked: boolean;
  notes: string | null;
  updated_at: string;
  updated_by: string;
  owner_team_member_id: string | null;
}

export function useChecklistItemStates(checklistId: string | undefined) {
  return useQuery<Record<string, ItemState>>({
    queryKey: ['checklist-items', checklistId],
    enabled: !!checklistId,
    queryFn: async () => {
      const data = await apiFetch<ItemState[]>(`/api/checklists/${checklistId}/items`);
      const out: Record<string, ItemState> = {};
      for (const r of data) out[r.item_id] = r;
      return out;
    },
  });
}

/** Every tick or owner save on one checklist shares this key: useIsMutating(toggleKey(id)) counts the ones in flight. */
export const toggleKey = (checklistId: string | undefined) => ['checklist-toggle', checklistId] as const;

/* Toggle a checklist item. Callers can send `checked`, `owner_team_member_id`
   (nullable to clear), or both. Optimistic update mirrors whichever fields
   were passed. The outcome of every save is reported through `on` (per-call
   mutate callbacks only reach the latest call), and a failed save undoes
   only its own item. */
export function useToggleChecklistItem(
  checklistId: string | undefined,
  on: { saved?: (itemId: string) => void; failed?: (message: string) => void } = {},
) {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: toggleKey(checklistId),
    mutationFn: async (args: {
      item_id: string;
      checked?: boolean;
      owner_team_member_id?: string | null;
    }) => {
      if (!checklistId) throw new Error('no checklist');
      const body: Record<string, unknown> = {};
      if (args.checked !== undefined) body.checked = args.checked;
      if (args.owner_team_member_id !== undefined)
        body.owner_team_member_id = args.owner_team_member_id;
      await apiFetch(`/api/checklists/${checklistId}/items/${args.item_id}`, {
        method: 'PUT',
        body,
      });
    },
    onMutate: async (args) => {
      if (!checklistId) return { prev: undefined };
      await qc.cancelQueries({ queryKey: ['checklist-items', checklistId] });
      const prev = qc.getQueryData<Record<string, ItemState>>([
        'checklist-items',
        checklistId,
      ])?.[args.item_id];
      /* Nothing written while the ticks haven't loaded: one optimistic tick would look like the whole set. */
      qc.setQueryData<Record<string, ItemState>>(
        ['checklist-items', checklistId],
        (old) => {
          if (!old) return old;
          const existing = old[args.item_id];
          return {
            ...old,
            [args.item_id]: {
              item_id: args.item_id,
              checked: args.checked ?? existing?.checked ?? false,
              notes: existing?.notes ?? null,
              updated_at: new Date().toISOString(),
              updated_by: existing?.updated_by ?? '',
              owner_team_member_id:
                args.owner_team_member_id !== undefined
                  ? args.owner_team_member_id
                  : (existing?.owner_team_member_id ?? null),
            },
          };
        },
      );
      return { prev };
    },
    onSuccess: (_data, args) => on.saved?.(args.item_id),
    onError: (err, args, ctx) => {
      if (checklistId) {
        qc.setQueryData<Record<string, ItemState>>(['checklist-items', checklistId], (old) => {
          if (!old) return old;
          const next = { ...old };
          if (ctx?.prev) next[args.item_id] = ctx.prev;
          else delete next[args.item_id];
          return next;
        });
      }
      on.failed?.(err instanceof Error ? err.message : String(err));
    },
    /* Read the ticks again after the last save in flight, not after each one: an earlier read
       could come back without a save still on its way, and briefly undo it on screen. */
    onSettled: () => {
      if (checklistId && qc.isMutating({ mutationKey: toggleKey(checklistId) }) <= 1) {
        qc.invalidateQueries({ queryKey: ['checklist-items', checklistId] });
      }
    },
  });
}

// ============================================================
// Team members (per-workspace directory)
// ============================================================

export type TeamMemberKind = 'PERSON' | 'LEA' | 'BILLING_VENDOR';

export interface TeamMember {
  id: string;
  name: string;
  email: string | null;
  department: string | null;
  kind: TeamMemberKind;
  created_at: string;
}

export function useTeamMembers(orgId: string | undefined) {
  return useQuery<TeamMember[]>({
    queryKey: ['team', orgId],
    enabled: !!orgId,
    queryFn: () => apiFetch<TeamMember[]>(`/api/workspaces/${orgId}/team`),
  });
}

export function useCreateTeamMember(orgId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      name: string;
      email?: string | null;
      department?: string | null;
    }) => {
      if (!orgId) throw new Error('no org');
      return apiFetch<TeamMember>(`/api/workspaces/${orgId}/team`, {
        method: 'POST',
        body: args,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['team', orgId] });
    },
  });
}

export function useDeleteTeamMember(orgId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      if (!orgId) throw new Error('no org');
      await apiFetch(`/api/workspaces/${orgId}/team/${id}`, {
        method: 'DELETE',
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['team', orgId] });
    },
  });
}

// ============================================================
// Founder-approval gate
// ============================================================

export function useMyApprovalStatus(userId: string | undefined) {
  return useQuery<boolean>({
    queryKey: ['am-i-approved', userId],
    enabled: !!userId,
    queryFn: async () => !!(await apiFetch<boolean>('/api/me/approved')),
  });
}

export function useIsAppAdmin(userId: string | undefined) {
  return useQuery<boolean>({
    queryKey: ['is-app-admin', userId],
    enabled: !!userId,
    queryFn: async () => !!(await apiFetch<boolean>('/api/me/admin')),
  });
}

export interface AdminStats {
  total_users: number;
  total_workspaces: number;
  pending_approvals: number;
  declined_users: number;
  total_assessments: number;
  group_assessments: number;
  groups_with_activity: number;
  signups_last_7_days: number;
  assessments_last_30_days: number;
}

export function useAdminStats(enabled: boolean) {
  return useQuery<AdminStats>({
    queryKey: ['admin-stats'],
    enabled,
    queryFn: () => apiFetch<AdminStats>('/api/manage/stats'),
  });
}

export interface PendingApproval {
  user_id: string;
  email: string;
  full_name: string | null;
  requested_at: string;
  email_confirmed_at: string | null;
}

export function usePendingApprovals(enabled: boolean) {
  return useQuery<PendingApproval[]>({
    queryKey: ['pending-approvals'],
    enabled,
    queryFn: () => apiFetch<PendingApproval[]>('/api/manage/pending'),
  });
}

export function useApproveUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (userId: string) => {
      await apiFetch('/api/manage/approve', {
        method: 'POST',
        body: { user_id: userId },
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pending-approvals'] });
    },
  });
}

export function useDeclineUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (userId: string) => {
      await apiFetch('/api/manage/decline', {
        method: 'POST',
        body: { user_id: userId },
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pending-approvals'] });
    },
  });
}

// ============================================================
// Workspace invites
// ============================================================

export interface InviteRow {
  id: string;
  org_id: string;
  email: string;
  role: OrgRole;
  token: string;
  created_at: string;
  expires_at: string;
  accepted_at: string | null;
}

export function useWorkspaceInvites(orgId: string | undefined) {
  return useQuery<InviteRow[]>({
    queryKey: ['workspace-invites', orgId],
    enabled: !!orgId,
    queryFn: () => apiFetch<InviteRow[]>(`/api/workspaces/${orgId}/invites`),
  });
}

export function useCreateInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { org_id: string; email: string; role?: OrgRole }) =>
      apiFetch<InviteRow>(`/api/workspaces/${args.org_id}/invites`, {
        method: 'POST',
        body: { email: args.email, role: args.role },
      }),
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: ['workspace-invites', row.org_id] });
    },
  });
}

export interface InviteLookup {
  org_id: string;
  org_name: string;
  role: OrgRole;
  email: string;
  expires_at: string;
  accepted_at: string | null;
}

export function useInviteLookup(token: string | undefined) {
  return useQuery<InviteLookup | null>({
    queryKey: ['invite-lookup', token],
    enabled: !!token,
    queryFn: () =>
      // The token goes in the body: request logs keep addresses (audit 3.15). A link that can't be an
      // invitation (tokens are 32 hex characters) is "no such invite" without asking.
      /^[0-9a-f]{32}$/i.test(token ?? '')
        ? apiFetch<InviteLookup | null>('/api/invites/lookup', {
            method: 'POST',
            body: { token },
            anonymous: true,
          })
        : Promise.resolve(null),
  });
}

export function useAcceptInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (token: string) =>
      apiFetch<string>('/api/invites/accept', {
        method: 'POST',
        body: { token },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['my-workspaces'] });
      qc.invalidateQueries({ queryKey: ['workspace-invites'] });
    },
  });
}

// ============================================================
// Workspace members (CYBHI)
// ============================================================

export function useWorkspaceMembers(orgId: string | undefined) {
  return useQuery<WorkspaceMember[]>({
    queryKey: ['workspace-members', orgId],
    enabled: !!orgId,
    queryFn: () => apiFetch<WorkspaceMember[]>(`/api/workspaces/${orgId}/members`),
  });
}
